
import {computed, defineComponent, onMounted, ref} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import {navigate} from '@/core/util/helper/navigation';

export default defineComponent({
  name: 'FinanceDashboard',
  setup() {
    const baseUrl = (window as unknown as {appGlobal: {baseUrl: string}})
      .appGlobal.baseUrl;
    const api = new APIService(baseUrl, '/api/v2/finance/dashboard');
    const now = new Date();
    const month = ref(now.getMonth() + 1);
    const year = ref(now.getFullYear());
    const loading = ref(true);
    const errorMessage = ref('');
    const snapshot = ref<any>(null);
    const months = Array.from({length: 12}, (_, index) => ({
      value: index + 1,
      label: new Intl.DateTimeFormat(undefined, {month: 'long'}).format(
        new Date(2026, index, 1),
      ),
    }));
    const years = Array.from(
      {length: 6},
      (_, index) => now.getFullYear() - index,
    );
    const currentPeriod = computed(() =>
      new Intl.DateTimeFormat(undefined, {
        month: 'long',
        year: 'numeric',
      }).format(new Date(year.value, month.value - 1, 1)),
    );
    const currencyTotals = computed(() =>
      (snapshot.value?.expenseTotals || []).map(
        (item: any) => `${item.currency} ${Number(item.total).toFixed(2)}`,
      ),
    );
    const chartColors = [
      '#397f83',
      '#e69b49',
      '#7386ba',
      '#7da986',
      '#a779a6',
      '#cb6c63',
    ];
    const trendMaximum = computed(() =>
      Math.max(
        1,
        ...(snapshot.value?.monthlyTrend || []).flatMap((item: any) => [
          Number(item.expenses),
          Number(item.payroll),
        ]),
      ),
    );
    const departmentMaximum = computed(() =>
      Math.max(
        1,
        ...(snapshot.value?.payrollByDepartment || []).map((item: any) =>
          Number(item.total),
        ),
      ),
    );
    const categoryGradient = computed(() => {
      const rows = snapshot.value?.expenseByCategory || [];
      const total = rows.reduce(
        (sum: number, item: any) => sum + Number(item.total),
        0,
      );
      if (!total) return '#e8eaed';
      let cursor = 0;
      return `conic-gradient(${rows
        .map((item: any, index: number) => {
          const next = cursor + (Number(item.total) / total) * 100;
          const part = `${
            chartColors[index % chartColors.length]
          } ${cursor}% ${next}%`;
          cursor = next;
          return part;
        })
        .join(',')})`;
    });
    const money = (value: string | number) =>
      Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    const barHeight = (value: string | number, maximum: number) =>
      `${Math.max(
        Number(value) > 0 ? 4 : 0,
        (Number(value) / maximum) * 100,
      )}%`;
    const shortMonth = (value: string) =>
      new Intl.DateTimeFormat(undefined, {month: 'short'}).format(
        new Date(`${value}T00:00:00`),
      );
    const cards = computed(() => [
      {
        title: 'Total expenses',
        value: currencyTotals.value.join(' · ') || '0.00',
        hint: currencyTotals.value.length
          ? 'Approved and paid this month'
          : 'No approved or paid expenses',
      },
      {
        title: 'Total payroll',
        value: Number(snapshot.value?.payroll?.gross || 0).toFixed(2),
        hint: 'Approved payroll gross',
      },
      {
        title: 'Net payroll',
        value: Number(snapshot.value?.payroll?.net || 0).toFixed(2),
        hint: 'Approved payroll net',
      },
      {
        title: 'Headcount',
        value: String(snapshot.value?.payroll?.headcount || 0),
        hint: 'Employees in approved runs',
      },
    ]);
    const loadDashboard = async () => {
      loading.value = true;
      errorMessage.value = '';
      try {
        const response = await api.getAll({
          month: month.value,
          year: year.value,
        });
        snapshot.value = response.data.data?.[0] || null;
      } catch (error: any) {
        errorMessage.value =
          error?.response?.data?.error?.message ||
          'Could not load Finance totals.';
      } finally {
        loading.value = false;
      }
    };
    const openExpenses = () => navigate('/finance/viewExpenses');
    const statusLabel = (status: string) =>
      (({pending_approval: 'Pending Approval'} as Record<string, string>)[
        status
      ] || status.charAt(0).toUpperCase() + status.slice(1));
    onMounted(loadDashboard);
    return {
      month,
      year,
      months,
      years,
      loading,
      errorMessage,
      snapshot,
      cards,
      currentPeriod,
      loadDashboard,
      openExpenses,
      statusLabel,
      chartColors,
      trendMaximum,
      departmentMaximum,
      categoryGradient,
      money,
      barHeight,
      shortMonth,
    };
  },
});
